import type { SagaCond } from './saga.ts';
import type { SendaDef, SendaLine } from './sendas.ts';
import { sec } from './units.ts';

/**
 * The mage's path, El libro que no ardió (docs/sendas/mago.md): the Book of the Tides went across the sea to be copied,
 * carried by Lio, Ansel's apprentice, in the Garza, with a ship's boy of fourteen; a storm laid them on the Peña. Isra,
 * the boy's sister, finds a page of it in a grouper's belly, and the mage goes down into the burnt library after it.
 *
 * Its numbers (BIBLIO) and its chapters live here, apart from the other paths (docs/SENDAS.md, «Para no chocar»).
 */

/**
 * The numbers of the burnt library (the mage's chapter 1, §3 and §4; the sim's battle/biblio.ts). Lives in seconds of D
 * and blows in bp of the hero's life (docs/SENDAS.md, «Calibración»), both times the chapter's `toughBp`.
 *
 * - `page`: the loose pages (soot crows drawn as black pages with ember edges): fliers that go for whoever carries
 *   Isra's page (the mage). `skeleton`, `wraith`: the cellars' risen (a skeleton out of the ash, an apparition).
 * - `scribe` (docs/SIMA.md): `d` of life; at the cabinet (or the ink lectern) it writes `write` (3 s, the rune grows)
 *   and seals the last seal broken again, then `rest` and writes again; killed while it writes, its ink breaks the ink
 *   seal. Walking there, every `rune.every` it writes a rune under the hero within `rune.range`: `rune.warn` later,
 *   whoever stands in `rune.r` takes `heroBp` and is cursed `rune.curse` (heals `rune.healBp` of what it would).
 * - `item`: the frost bowls, acid flasks and braziers by each seal break to a bolt from within `reach` (de un golpe,
 *   up close) and leave their state on the seal next to them; back `refill` later (by act). A seal takes a bolt from
 *   the hero's own reach.
 */
export const BIBLIO = {
  page: { d: 1, heroBp: 500, reload: 16 },
  skeleton: { d: 3, heroBp: 650 },
  wraith: { d: 2, heroBp: 500 },
  scribe: {
    d: 5,
    heroBp: 800,
    pace: 55,
    write: sec(3),
    rest: sec(2),
    rune: {
      every: sec(5),
      warn: sec(3),
      range: 6500,
      r: 750,
      curse: sec(5),
      healBp: 5000,
    },
  },
  item: { reach: 2300 },
  /**
   * The burnt cellars (act 1, battle/biblio.ts): four halls down to the catalog (won within `reach` of it). In each hall
   * piles of ash and bones (`piles`, by hall) that rise as the hero comes within `wake` (the page in its hand glows
   * first, `glow` out); the stair down is blocked by fallen beams while one risen of the hall stands. Loose pages come
   * off the burning shelves of the hero's hall: `n` every `every` from `first`.
   */
  soot: {
    reach: 1600,
    wake: 3400,
    glow: 6000,
    piles: [3, 4, 4, 5],
    wraithEvery: 2,
    pages: { first: sec(9), every: sec(14), n: 4 },
    hpBp: 10000,
    dmgBp: 10000,
  },
  /**
   * The three locks (act 2, battle/biblio.ts; §4, «Los cerrojos»): three lecterns, each with its frost bowl, acid flask
   * and brazier (back `refill` after breaking); no pressure till two are open: then `pages.n` pages every
   * `pages.every`, and a scribe every `scribeEvery` from the door to the ink lectern (the first `scribeFirst` after).
   */
  locks: {
    refill: sec(10),
    pages: { n: 6, every: sec(8) },
    scribeFirst: sec(6),
    scribeEvery: sec(15),
    hpBp: 13000,
    dmgBp: 16000,
  },
  /**
   * The fire room (act 3, the test; battle/biblio.ts): Ansel's iron cabinet in the middle, its three seals each facing
   * a shelf with its bowl, flask and brazier (back `refill`). Six walls of fire on the spokes between the shelves rise
   * in a fixed turn: one every `gap` from `first`, each up `up`, the floor red `warn` before. Caught on one as it
   * rises, `caughtBp` and thrown off; standing within `heatR` of one up, `heatBp` a second; anywhere in the room the
   * smoke, `smokeBp` a second (and no rest: the hero doesn't heal by itself in here). Pages: `n` every `every`
   * from `first`; a scribe every `scribeEvery` from `scribeFirst`, by a door in the wall. Lost at `lose` seals sealed
   * again. A seal broken alone closes again by itself `hold` after (the three must break at once).
   */
  cabinet: {
    refill: sec(20),
    spokes: {
      first: sec(6),
      gap: sec(4),
      up: sec(8),
      warn: sec(3),
      caughtBp: 3000,
      heatBp: 220,
      heatR: 1150,
      smokeBp: 150,
    },
    pages: { first: sec(4), every: sec(12), n: 4 },
    scribeFirst: sec(6),
    scribeEvery: sec(15),
    lose: 3,
    hold: sec(36),
    hpBp: 10000,
    dmgBp: 9000,
  },
};

const ln = (id: string, who: string, cond?: SagaCond): SendaLine => (cond ? { id, who, if: cond } : { id, who });
/** The group played La Orilla (Yorra knows Isra); else Brann brings her in. */
const SHORE: SagaCond = { flag: '@orilla' };
const NO_SHORE: SagaCond = { notFlag: '@orilla' };
/** Asked at the campfire about the Garza: Isra already said her brother was its ship's boy. */
const GARZA: SagaCond = { flag: 'askedGarza' };
const NO_GARZA: SagaCond = { notFlag: 'askedGarza' };

/**
 * The mage's path. Chapter 1, «Ceniza» (level 18): Isra opens a grouper on the Terca's gunwale and finds a page, dry,
 * its ink glowing like embers by the Order's open eye; in the Laboratory (the yard where there is none) she brings it,
 * and Brann knows it from the library buried the night of the fall. Down through its burnt cellars (four halls, the
 * page glowing before the eye sees, the loose pages after it), the catalog's bronze drawers (Ansel's hand: «Lo lleva
 * Lio»), the three lecterns and their seals (frost, crust and ink: each broken by what readies it), the campfire at the
 * collapse's mouth with Isra, and the test, the fire room (Ansel's cabinet, its three seals, the walls of fire in their
 * turn, the scribes that seal it again). Closing with two notes and a burnt clasp, and on the pier a man with white
 * hair who remembers where to find the Terca.
 */
export const MAGE_PATH: SendaDef = {
  id: 'mage',
  cls: 'mage',
  planned: 5,
  cast: {
    isra: { model: 'isra' },
    brann: { npc: 'brann' },
    yorra: { npc: 'yorra' },
    // The catalog's drawer in Ansel's hand; Ansel's note to Lio; Lio's below it, in another hand.
    drawer: { voice: true },
    anselNote: { voice: true },
    lioNote: { voice: true },
    // The man on the pier (Ser Hadric, not named yet).
    stranger: { model: 'hadric' },
  },
  chapters: [
    {
      id: 'ash',
      closedBy: 'isra',
      piece: 'burntClasp',
      acts: [
        {
          // A fisherwoman opens a big fish on a sloop's gunwale: inside, folded, a page of thick paper, dry; its ink
          // glows red like an ember, and beside it the Order's seal, an open eye.
          kind: 'scene',
          id: 'cine',
          lines: [],
          cine: {
            video: 'senda-mage-1',
            place: 'stilts',
            sky: 'night',
            cam: [
              { from: [-3.4, 6.4, 3.0], at: [0.4, -0.6, 0.6], ms: 5200 },
              { from: [-1.2, 3.0, 1.4], at: [0.2, 0.2, 0.55], ms: 5200 },
              { from: [0.9, 1.6, 0.95], at: [0, 0.1, 0.45], ms: 5000 },
            ],
            figures: [{ who: 'isra', q: 0, r: 0, facing: 4.4, pose: 'kneel' }],
          },
        },
        {
          kind: 'scene',
          id: 'open',
          lines: [ln('y1', 'yorra', SHORE), ln('b1', 'brann', NO_SHORE), ln('l2', 'isra'), ln('l3', 'isra'), ln('l4', 'brann'), ln('l5', 'isra')],
          cine: {
            place: 'labYard',
            sky: 'dusk',
            cam: [
              { from: [-3.2, 6.4, 2.5], at: [0.4, -0.8, 0.9], ms: 8000 },
              { from: [-1.2, 3.0, 1.3], at: [0.3, 0, 0.6], ms: 9000 },
              { from: [1.4, 2.2, 1.0], at: [-0.2, 0.2, 0.45], ms: 9000 },
            ],
            figures: [
              { who: 'isra', q: 0, r: 0, facing: 4.6, pose: 'stand' },
              { who: 'brann', q: -1, r: 0, facing: 0.3, pose: 'stand' },
              {
                who: 'yorra',
                q: -1,
                r: 1,
                facing: 0.6,
                pose: 'stand',
                if: SHORE,
              },
              { who: 'hero', q: 1, r: 0, facing: 3.1, pose: 'stand' },
            ],
            structures: [{ type: 'lab', q: 0, r: -3 }],
            alt: {
              unless: 'lab',
              cine: {
                place: 'labYard',
                sky: 'dusk',
                cam: [
                  { from: [-3.2, 6.4, 2.5], at: [0.4, -0.8, 0.9], ms: 8000 },
                  { from: [-1.2, 3.0, 1.3], at: [0.3, 0, 0.6], ms: 9000 },
                  { from: [1.4, 2.2, 1.0], at: [-0.2, 0.2, 0.45], ms: 9000 },
                ],
                figures: [
                  { who: 'isra', q: 0, r: 0, facing: 4.6, pose: 'stand' },
                  { who: 'brann', q: -1, r: 0, facing: 0.3, pose: 'stand' },
                  {
                    who: 'yorra',
                    q: -1,
                    r: 1,
                    facing: 0.6,
                    pose: 'stand',
                    if: SHORE,
                  },
                  { who: 'hero', q: 1, r: 0, facing: 3.1, pose: 'stand' },
                ],
              },
            },
          },
        },
        {
          // The burnt cellars: four halls of stone black with soot, fallen shelves, a collapse of beams at the way in;
          // the page in the hero's hand glows when something is near (§3, act 1).
          kind: 'fight',
          id: 'cellars',
          mode: 'soot',
          place: 'sootCellars',
          budgetBp: 9000,
          goal: { kind: 'catalog' },
          says: [
            { on: 'start', who: 'isra', line: 'l1', delay: sec(2) },
            {
              on: 'cue',
              cue: 'pages1',
              who: 'isra',
              line: 'l2',
              delay: sec(1),
            },
            { on: 'cue', cue: 'hall3', who: 'isra', line: 'l3' },
          ],
        },
        {
          // A wall of bronze drawers, black with soot; one alone wiped clean, in Ansel's hand.
          kind: 'scene',
          id: 'catalog',
          lines: [ln('l1', 'narrator'), ln('l2', 'drawer'), ln('l3', 'narrator')],
          cine: {
            place: 'catalog',
            sky: 'night',
            cam: [
              { from: [-2.4, 4.8, 2.0], at: [0.4, -1.6, 1.0], ms: 9000 },
              { from: [0.6, 2.0, 1.1], at: [0, -1.6, 0.9], ms: 9000 },
            ],
            figures: [{ who: 'hero', q: 0, r: 0, facing: 1.5, pose: 'stand' }],
          },
        },
        {
          // Three iron lecterns with sealed books: one under blue frost, one under a green crust that smokes, one
          // wound in threads of glowing ink (§3, act 2; §4, «Los cerrojos»).
          kind: 'fight',
          id: 'locks',
          mode: 'lecterns',
          place: 'lecterns',
          budgetBp: 9000,
          goal: { kind: 'locks' },
          says: [
            { on: 'start', who: 'isra', line: 'l1', delay: sec(3) },
            { on: 'cue', cue: 'open1', who: 'isra', line: 'l2', delay: sec(1) },
            {
              on: 'cue',
              cue: 'scribe1',
              who: 'isra',
              line: 'l3',
              delay: sec(1),
            },
            { on: 'cue', cue: 'reseal1', who: 'isra', line: 'l4' },
          ],
        },
        {
          kind: 'fire',
          id: 'fire',
          with: ['isra'],
          questions: [
            {
              id: 'far',
              lines: [ln('l1', 'isra'), ln('l2', 'isra')],
              set: ['askedFar'],
            },
            {
              id: 'garza',
              lines: [ln('l1', 'isra'), ln('l2', 'isra')],
              set: ['askedGarza'],
            },
            { id: 'order', lines: [ln('l1', 'isra')], set: ['askedOrder'] },
          ],
          cine: {
            place: 'collapse',
            sky: 'night',
            cam: [
              { from: [2.6, 3.0, 2.1], at: [-0.3, -0.3, 0.35], ms: 9000 },
              { from: [-1.6, 3.2, 1.6], at: [0.1, -0.3, 0.4], ms: 9000 },
            ],
            figures: [
              { who: 'isra', q: -1, r: 1, facing: 5.4, pose: 'sit' },
              { who: 'hero', q: 1, r: -1, facing: 2.2, pose: 'sit' },
            ],
            fire: { q: 0, r: 0 },
          },
        },
        {
          // The test: the room where the fire began; Ansel's iron cabinet with three seals, the walls of fire in their
          // fixed turn, the scribes that come to seal it again (§3, act 3).
          kind: 'fight',
          id: 'cabinet',
          mode: 'cabinet',
          place: 'fireRoom',
          budgetBp: 9000,
          goal: { kind: 'cabinet' },
          says: [
            { on: 'start', who: 'isra', line: 'l1', delay: sec(3) },
            { on: 'cue', cue: 'seal1', who: 'isra', line: 'l2', delay: sec(1) },
            { on: 'cue', cue: 'reseal1', who: 'isra', line: 'l3' },
            { on: 'cue', cue: 'writing1', who: 'isra', line: 'l4' },
          ],
          trial: true,
        },
        {
          kind: 'scene',
          id: 'close',
          lines: [ln('l1', 'narrator'), ln('l2', 'narrator'), ln('l3', 'anselNote'), ln('l4', 'lioNote'), ln('l5', 'isra'), ln('g6', 'isra', GARZA), ln('n6', 'isra', NO_GARZA), ln('l7', 'narrator')],
          cine: {
            place: 'fireOut',
            sky: 'night',
            cam: [
              { from: [-2.6, 5.6, 2.8], at: [0.2, -0.4, 1.0], ms: 9000 },
              { from: [-0.8, 2.4, 1.3], at: [0.1, -0.2, 0.9], ms: 9000 },
              { from: [1.4, 1.6, 1.0], at: [-0.2, 0.1, 0.7], ms: 9000 },
            ],
            figures: [
              { who: 'hero', q: 0, r: 2, facing: 1.5, pose: 'stand' },
              { who: 'isra', q: -1, r: 2, facing: 1.3, pose: 'stand' },
            ],
          },
        },
        {
          // Back at the stilts, on the pier, a man with white hair and a grey cape waits sitting on a crate.
          kind: 'scene',
          id: 'pier',
          lines: [ln('l1', 'narrator'), ln('l2', 'stranger'), ln('l3', 'isra'), ln('l4', 'stranger')],
          cine: {
            place: 'pier',
            sky: 'dawn',
            cam: [
              { from: [-3.0, 5.6, 2.4], at: [0.6, -1.0, 0.8], ms: 8000 },
              { from: [-1.0, 2.6, 1.2], at: [0.3, -0.4, 0.6], ms: 9000 },
              { from: [1.2, 2.0, 0.95], at: [-0.3, -0.6, 0.55], ms: 9000 },
            ],
            figures: [
              { who: 'stranger', q: 0, r: -1, facing: 1.5, pose: 'sit' },
              { who: 'isra', q: 0, r: 1, facing: 4.6, pose: 'stand' },
              { who: 'hero', q: 1, r: 1, facing: 4.2, pose: 'stand' },
            ],
          },
        },
      ],
    },
  ],
};
